// SPDX-License-Identifier: AGPL-3.0-or-later

import Accessibility from '@app/features/accessibility/state/Accessibility';
import {isKeyboardActivationKey} from '@app/features/input/utils/KeyboardUtils';
import styles from '@app/features/ui/action_menu/ContextMenu.module.css';
import {Scroller} from '@app/features/ui/components/Scroller';
import {resolvePortalHost, usePortalHost} from '@app/features/ui/overlay/PortalHostContext';
import type {ContextMenu as ContextMenuType} from '@app/features/ui/state/ContextMenu';
import ContextMenuState, {isContextMenuNodeTarget} from '@app/features/ui/state/ContextMenu';
import LayerManager from '@app/features/ui/state/LayerManager';
import {appZoomLayoutPx} from '@app/features/ui/utils/AppZoomUtils';
import {isMobileExperienceEnabled} from '@app/features/ui/utils/MobileExperience';
import {isScrollbarDragActive} from '@app/features/ui/utils/ScrollbarDragState';
import {canUseWindowFocusedHoverControls} from '@app/features/ui/utils/WindowFocusInteractionGuard';
import {wasPointerDownInside} from '@app/lib/overlay/DismissGuard';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import React, {useCallback, useContext, useEffect, useId, useLayoutEffect, useRef, useState} from 'react';
import type {PressEvent} from 'react-aria-components';
import {
	Menu as AriaMenu,
	MenuItem as AriaMenuItem,
	MenuSection as AriaMenuSection,
	Popover as AriaPopover,
	Separator as AriaSeparator,
	SubmenuTrigger as AriaSubmenuTrigger,
} from 'react-aria-components';
import {createPortal} from 'react-dom';

const CONTEXT_MENU_DESCRIPTOR = msg({
	message: 'Context menu',
	comment: 'Accessible label for a context menu container.',
});
const SUBMENU_DESCRIPTOR = msg({
	message: 'Submenu',
	comment: 'Accessible label for a context menu submenu container.',
});
const ContextMenuCloseContext = React.createContext<() => void>(() => {});
const SubMenuDepthContext = React.createContext(0);
export const useContextMenuClose = () => useContext(ContextMenuCloseContext);
export const ContextMenuCloseProvider = ContextMenuCloseContext.Provider;

function stopHoverEventWhenWindowCannotUseHover(event: React.SyntheticEvent): void {
	if (canUseWindowFocusedHoverControls()) return;
	event.stopPropagation();
}

interface SelectableMenuItemProps {
	selectionMode: 'single' | 'multiple';
	selected: boolean;
	onAction: () => void;
	isDisabled?: boolean;
	shouldCloseOnSelect?: boolean;
	className?: string;
	textValue: string;
	children: React.ReactNode;
}

export const SelectableMenuItem = React.forwardRef<HTMLDivElement, SelectableMenuItemProps>(
	(
		{selectionMode, selected, onAction, isDisabled, shouldCloseOnSelect, className, textValue, children},
		forwardedRef,
	) => {
		const itemKey = useId();
		return (
			<AriaMenuSection
				className={styles.selectableSection}
				selectionMode={selectionMode}
				selectedKeys={selected ? [itemKey] : []}
				data-flx="ui.action-menu.context-menu.selectable-menu-item.selectable-section"
			>
				<AriaMenuItem
					ref={forwardedRef}
					id={itemKey}
					onAction={onAction}
					isDisabled={isDisabled}
					shouldCloseOnSelect={shouldCloseOnSelect}
					className={className}
					textValue={textValue}
					data-flx="ui.action-menu.context-menu.selectable-menu-item.aria-menu-item"
				>
					{children}
				</AriaMenuItem>
			</AriaMenuSection>
		);
	},
);

SelectableMenuItem.displayName = 'SelectableMenuItem';

const menuItemSelector = [
	'[role="menuitem"]:not([aria-disabled="true"])',
	'[role="menuitemcheckbox"]:not([aria-disabled="true"])',
	'[role="menuitemradio"]:not([aria-disabled="true"])',
].join(', ');
type ContextMenuAlign = NonNullable<NonNullable<ContextMenuType['config']>['align']>;

const CONTEXT_MENU_EDGE_PADDING = 12;
const CONTEXT_MENU_OFFSET = 4;
const MENU_SURFACE_PADDING = 8;
const SUBMENU_SURFACE_GAP = 6;
const SUBMENU_OFFSET = MENU_SURFACE_PADDING + SUBMENU_SURFACE_GAP;
const textValueFromNode = (node: React.ReactNode): string => {
	if (typeof node === 'string') return node;
	if (typeof node === 'number') return String(node);
	if (Array.isArray(node)) return node.map(textValueFromNode).join('');
	if (React.isValidElement(node)) {
		return textValueFromNode((node.props as {children?: React.ReactNode}).children);
	}
	return '';
};

interface RootContextMenuProps {
	contextMenu: ContextMenuType;
}

interface ContextMenuPoint {
	x: number;
	y: number;
}

interface ContextMenuSize {
	width: number;
	height: number;
}

interface ContextMenuViewportFitInput {
	point: ContextMenuPoint;
	size: ContextMenuSize;
	viewport: ContextMenuSize;
	align: ContextMenuAlign;
	edgePadding?: number;
	offset?: number;
}

interface ContextMenuViewportFit {
	x: number;
	y: number;
	maxWidth: number | null;
	maxHeight: number | null;
}

type ContextMenuHorizontalAlign = 'left' | 'right';
type ContextMenuVerticalAlign = 'top' | 'bottom';

interface AxisFit {
	start: number;
	maxSize: number | null;
}

const oppositeHorizontalAlign = (align: ContextMenuHorizontalAlign): ContextMenuHorizontalAlign =>
	align === 'left' ? 'right' : 'left';
const oppositeVerticalAlign = (align: ContextMenuVerticalAlign): ContextMenuVerticalAlign =>
	align === 'top' ? 'bottom' : 'top';

function clamp(value: number, min: number, max: number): number {
	return Math.max(min, Math.min(value, max));
}

function splitAlign(align: ContextMenuAlign): {
	vertical: ContextMenuVerticalAlign;
	horizontal: ContextMenuHorizontalAlign;
} {
	const [vertical, horizontal] = align.split('-') as [ContextMenuVerticalAlign, ContextMenuHorizontalAlign];
	return {vertical, horizontal};
}

function getHorizontalStart(pointX: number, width: number, align: ContextMenuHorizontalAlign): number {
	return align === 'left' ? pointX : pointX - width;
}

function getVerticalStart(
	pointY: number,
	height: number,
	align: ContextMenuVerticalAlign,
	offsetValue: number,
): number {
	return align === 'top' ? pointY + offsetValue : pointY - height - offsetValue;
}

function fitsAxis(start: number, size: number, minStart: number, maxEnd: number): boolean {
	return start >= minStart && start + size <= maxEnd;
}

function fitAxisToViewport({
	preferredStart,
	alternateStart,
	size,
	viewportSize,
	edgePadding,
}: {
	preferredStart: number;
	alternateStart: number;
	size: number;
	viewportSize: number;
	edgePadding: number;
}): AxisFit {
	const minStart = Math.max(0, Math.min(edgePadding, viewportSize));
	const maxEnd = Math.max(minStart, viewportSize - edgePadding);
	const availableSize = Math.max(0, maxEnd - minStart);
	if (size > availableSize) {
		return {start: minStart, maxSize: availableSize};
	}
	const maxStart = Math.max(minStart, maxEnd - size);
	if (fitsAxis(preferredStart, size, minStart, maxEnd)) {
		return {start: preferredStart, maxSize: null};
	}
	if (fitsAxis(alternateStart, size, minStart, maxEnd)) {
		return {start: alternateStart, maxSize: null};
	}
	return {start: clamp(preferredStart, minStart, maxStart), maxSize: null};
}

export function fitContextMenuToViewport({
	point,
	size,
	viewport,
	align,
	edgePadding = CONTEXT_MENU_EDGE_PADDING,
	offset: offsetValue = CONTEXT_MENU_OFFSET,
}: ContextMenuViewportFitInput): ContextMenuViewportFit {
	const {vertical, horizontal} = splitAlign(align);
	const horizontalFit = fitAxisToViewport({
		preferredStart: getHorizontalStart(point.x, size.width, horizontal),
		alternateStart: getHorizontalStart(point.x, size.width, oppositeHorizontalAlign(horizontal)),
		size: size.width,
		viewportSize: viewport.width,
		edgePadding,
	});
	const verticalFit = fitAxisToViewport({
		preferredStart: getVerticalStart(point.y, size.height, vertical, offsetValue),
		alternateStart: getVerticalStart(point.y, size.height, oppositeVerticalAlign(vertical), offsetValue),
		size: size.height,
		viewportSize: viewport.height,
		edgePadding,
	});
	return {
		x: horizontalFit.start,
		y: verticalFit.start,
		maxWidth: horizontalFit.maxSize,
		maxHeight: verticalFit.maxSize,
	};
}

interface RootContextMenuLayout extends ContextMenuViewportFit {
	isPositioned: boolean;
}

type RootContextMenuStyle = React.CSSProperties;

function areRootContextMenuLayoutsEqual(a: RootContextMenuLayout, b: RootContextMenuLayout): boolean {
	return (
		a.x === b.x &&
		a.y === b.y &&
		a.maxWidth === b.maxWidth &&
		a.maxHeight === b.maxHeight &&
		a.isPositioned === b.isPositioned
	);
}

function getViewportSize(): ContextMenuSize {
	return {
		width: window.innerWidth || document.documentElement.clientWidth || 0,
		height: window.innerHeight || document.documentElement.clientHeight || 0,
	};
}

function measureRootContextMenu(menuElement: HTMLElement): ContextMenuSize {
	const menuRect = menuElement.getBoundingClientRect();
	const scroller = menuElement.querySelector<HTMLElement>('[data-fluxer-scroll-container="true"]');
	const measuredWidth = menuRect.width || menuElement.offsetWidth;
	if (!scroller) {
		return {
			width: Math.max(1, Math.ceil(measuredWidth)),
			height: Math.max(1, Math.ceil(menuRect.height || menuElement.offsetHeight)),
		};
	}
	const scrollerRect = scroller.getBoundingClientRect();
	const chromeHeight = Math.max(0, menuRect.height - scrollerRect.height);
	const measuredHeight = Math.max(menuRect.height, chromeHeight + scroller.scrollHeight);
	return {
		width: Math.max(1, Math.ceil(measuredWidth)),
		height: Math.max(1, Math.ceil(measuredHeight)),
	};
}

const RootContextMenuInner: React.FC<RootContextMenuProps> = observer(({contextMenu}) => {
	const {i18n} = useLingui();
	const [isOpen, setIsOpen] = useState(true);
	const [layout, setLayout] = useState<RootContextMenuLayout>(() => ({
		x: contextMenu.target.x,
		y: contextMenu.target.y,
		maxWidth: null,
		maxHeight: null,
		isPositioned: false,
	}));
	const menuContentRef = useRef<HTMLDivElement>(null);
	const menuElementRef = useRef<HTMLDivElement>(null);
	const layoutRafRef = useRef<number | null>(null);
	const syncLayoutPassesRef = useRef(0);
	const align = contextMenu.config?.align ?? 'top-left';
	const anchorElement =
		contextMenu.config?.trackDynamicPosition && isContextMenuNodeTarget(contextMenu.target.target)
			? contextMenu.target.target
			: null;
	const anchorOffsetRef = useRef<{dx: number; dy: number} | null>(null);
	const getReferencePoint = useCallback((): ContextMenuPoint => {
		const {x, y} = contextMenu.target;
		if (!anchorElement?.isConnected) {
			return {x, y};
		}
		const rect = anchorElement.getBoundingClientRect();
		if (!anchorOffsetRef.current) {
			anchorOffsetRef.current = {dx: x - rect.left, dy: y - rect.top};
		}
		return {
			x: rect.left + anchorOffsetRef.current.dx,
			y: rect.top + anchorOffsetRef.current.dy,
		};
	}, [contextMenu.target, anchorElement]);
	const updateLayout = useCallback(() => {
		const menuElement = menuElementRef.current;
		if (!menuElement) {
			return;
		}
		const nextLayout: RootContextMenuLayout = {
			...fitContextMenuToViewport({
				point: getReferencePoint(),
				size: measureRootContextMenu(menuElement),
				viewport: getViewportSize(),
				align,
			}),
			isPositioned: true,
		};
		setLayout((currentLayout) =>
			areRootContextMenuLayoutsEqual(currentLayout, nextLayout) ? currentLayout : nextLayout,
		);
	}, [align, getReferencePoint]);
	const scheduleLayoutUpdate = useCallback(() => {
		if (layoutRafRef.current != null) {
			return;
		}
		layoutRafRef.current = window.requestAnimationFrame(() => {
			layoutRafRef.current = null;
			updateLayout();
		});
	}, [updateLayout]);
	useLayoutEffect(() => {
		if (!menuElementRef.current || syncLayoutPassesRef.current >= 2) {
			return;
		}
		syncLayoutPassesRef.current += 1;
		updateLayout();
	});
	useLayoutEffect(() => {
		if (!isOpen) {
			return;
		}
		const menuElement = menuElementRef.current;
		if (!menuElement) {
			return;
		}
		const resizeObserver =
			typeof ResizeObserver === 'function'
				? new ResizeObserver(() => {
						scheduleLayoutUpdate();
					})
				: null;
		resizeObserver?.observe(menuElement);
		if (menuContentRef.current) {
			resizeObserver?.observe(menuContentRef.current);
		}
		if (anchorElement?.isConnected) {
			resizeObserver?.observe(anchorElement);
		}
		window.addEventListener('resize', scheduleLayoutUpdate);
		window.addEventListener('scroll', scheduleLayoutUpdate, true);
		return () => {
			resizeObserver?.disconnect();
			window.removeEventListener('resize', scheduleLayoutUpdate);
			window.removeEventListener('scroll', scheduleLayoutUpdate, true);
		};
	}, [anchorElement, isOpen, scheduleLayoutUpdate]);
	useEffect(() => {
		return () => {
			if (layoutRafRef.current != null) {
				window.cancelAnimationFrame(layoutRafRef.current);
				layoutRafRef.current = null;
			}
		};
	}, []);
	const focusMenuItemAtBoundary = useCallback((boundary: 'first' | 'last') => {
		const menuElement = menuContentRef.current;
		if (!menuElement) {
			return false;
		}
		const interactableItems = menuElement.querySelectorAll<HTMLElement>(menuItemSelector);
		const menuItem = boundary === 'last' ? interactableItems[interactableItems.length - 1] : interactableItems[0];
		(menuItem ?? menuElement).focus({preventScroll: true});
		return true;
	}, []);
	const close = useCallback(() => {
		setIsOpen(false);
		ContextMenuState.closeById(contextMenu.id);
	}, [contextMenu.id]);
	useEffect(() => {
		if (isOpen) {
			LayerManager.addLayer('contextmenu', contextMenu.id, close);
			return () => {
				LayerManager.removeLayer('contextmenu', contextMenu.id);
			};
		}
		return;
	}, [isOpen, contextMenu.id, close]);
	const handleBackdropClick = useCallback(() => {
		if (isScrollbarDragActive()) {
			return;
		}
		if (wasPointerDownInside(menuElementRef.current)) {
			return;
		}
		close();
	}, [close]);
	const handleBackdropMouseDown = useCallback(
		(event: React.MouseEvent<HTMLDivElement>) => {
			if (isScrollbarDragActive()) {
				return;
			}
			if (wasPointerDownInside(menuElementRef.current)) {
				return;
			}
			if (contextMenu.config?.onBackdropMouseDown?.(event)) {
				event.preventDefault();
				event.stopPropagation();
			}
		},
		[contextMenu.config],
	);
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				close();
				return;
			}
			const menuElement = menuContentRef.current;
			if (!menuElement) {
				return;
			}
			const activeElement = document.activeElement;
			const isFocusInsideSubmenu =
				activeElement instanceof HTMLElement && Boolean(activeElement.closest(`.${styles.submenuPopover}`));
			const isFocusInsideMenu =
				activeElement instanceof HTMLElement && (menuElement.contains(activeElement) || isFocusInsideSubmenu);
			if (!isFocusInsideMenu) {
				if (e.key === 'ArrowDown' || e.key === 'Home') {
					if (focusMenuItemAtBoundary('first')) {
						e.preventDefault();
						e.stopPropagation();
					}
					return;
				}
				if (e.key === 'ArrowUp' || e.key === 'End') {
					if (focusMenuItemAtBoundary('last')) {
						e.preventDefault();
						e.stopPropagation();
					}
					return;
				}
			}
			const eventTarget = e.target;
			const isEditableTarget =
				eventTarget instanceof HTMLElement &&
				(eventTarget instanceof HTMLInputElement ||
					eventTarget instanceof HTMLTextAreaElement ||
					eventTarget.isContentEditable);
			if (!isEditableTarget && !isFocusInsideSubmenu && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
				const menuItems = menuElement.querySelectorAll<HTMLElement>(menuItemSelector);
				const pressedKey = e.key.toLowerCase();
				for (const item of menuItems) {
					const itemShortcut = item.dataset.menuShortcut?.toLowerCase().trim();
					const shortcutElement =
						item.querySelector(`.${styles.itemShortcut}`) || item.querySelector(`.${styles.itemShortcutHidden}`);
					const shortcutText = itemShortcut || shortcutElement?.textContent?.toLowerCase().trim();
					if (shortcutText === pressedKey) {
						e.preventDefault();
						e.stopPropagation();
						item.focus({preventScroll: true});
						item.dispatchEvent(
							new KeyboardEvent('keydown', {key: 'Enter', code: 'Enter', bubbles: true, cancelable: true}),
						);
						item.dispatchEvent(
							new KeyboardEvent('keyup', {key: 'Enter', code: 'Enter', bubbles: true, cancelable: true}),
						);
						return;
					}
				}
			}
		};
		const handleClickOutside = (e: MouseEvent) => {
			if (isScrollbarDragActive()) {
				return;
			}
			if (wasPointerDownInside(menuElementRef.current)) {
				return;
			}
			const targetElement = e.target as HTMLElement;
			if (menuElementRef.current && !menuElementRef.current.contains(targetElement)) {
				if (targetElement.closest?.(`.${styles.submenuPopover}`)) {
					return;
				}
				if (targetElement.closest?.('[data-context-menu-root="true"]')) {
					return;
				}
				close();
			}
		};
		document.addEventListener('keydown', handleKeyDown);
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('keydown', handleKeyDown);
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [close, focusMenuItemAtBoundary]);
	if (!isOpen) {
		return null;
	}
	const menuStyle: RootContextMenuStyle = {
		position: 'fixed',
		left: `${appZoomLayoutPx(layout.x)}px`,
		top: `${appZoomLayoutPx(layout.y)}px`,
		visibility: layout.isPositioned ? 'visible' : 'hidden',
		pointerEvents: 'auto',
		zIndex: 'var(--z-index-contextmenu)',
	};
	if (layout.maxWidth != null) {
		menuStyle.maxWidth = `${appZoomLayoutPx(layout.maxWidth)}px`;
	}
	if (layout.maxHeight != null) {
		menuStyle.maxHeight = `${appZoomLayoutPx(layout.maxHeight)}px`;
	}
	return (
		<div
			className={styles.contextMenuOverlay}
			data-overlay-pass-through="true"
			data-flx="ui.action-menu.context-menu.root-context-menu-inner.context-menu-overlay"
		>
			<div
				className={styles.backdrop}
				onMouseDown={handleBackdropMouseDown}
				onClick={handleBackdropClick}
				aria-hidden="true"
				data-flx="ui.action-menu.context-menu.root-context-menu-inner.backdrop"
			/>
			<div
				ref={menuElementRef}
				data-context-menu-root="true"
				className={styles.contextMenu}
				style={menuStyle}
				onMouseMoveCapture={stopHoverEventWhenWindowCannotUseHover}
				onMouseOverCapture={stopHoverEventWhenWindowCannotUseHover}
				onPointerMoveCapture={stopHoverEventWhenWindowCannotUseHover}
				onPointerOverCapture={stopHoverEventWhenWindowCannotUseHover}
				data-flx="ui.action-menu.context-menu.root-context-menu-inner.context-menu"
			>
				<ContextMenuCloseContext.Provider value={close}>
					<Scroller
						className={styles.menuScroller}
						contentClassName={styles.menuScrollerContent}
						overflow="auto"
						fade={false}
						key={`context-menu-scroller-${contextMenu.id}`}
						data-flx="ui.action-menu.context-menu.root-context-menu-inner.menu-scroller"
					>
						<AriaMenu
							ref={menuContentRef}
							className={styles.ariaMenu}
							aria-label={i18n._(CONTEXT_MENU_DESCRIPTOR)}
							data-flx="ui.action-menu.context-menu.root-context-menu-inner.aria-menu"
						>
							{contextMenu.render({onClose: close})}
						</AriaMenu>
					</Scroller>
				</ContextMenuCloseContext.Provider>
			</div>
		</div>
	);
});
export const RootContextMenu: React.FC<RootContextMenuProps> = observer(({contextMenu}) => {
	return (
		<RootContextMenuInner
			contextMenu={contextMenu}
			data-flx="ui.action-menu.context-menu.root-context-menu.root-context-menu-inner"
		/>
	);
});

interface MenuItemProps {
	label: string;
	disabled?: boolean;
	onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
	onSelect?: (event: PressEvent) => void;
	icon?: React.ReactNode;
	danger?: boolean;
	color?: string;
	className?: string;
	children?: React.ReactNode;
	closeOnSelect?: boolean;
	shortcut?: React.ReactNode;
	hint?: React.ReactNode;
}

export const MenuItem = React.forwardRef<HTMLDivElement, MenuItemProps>(
	(
		{label, disabled, onSelect, icon, danger, className, children, closeOnSelect = true, shortcut, hint},
		forwardedRef,
	) => {
		const closeMenu = useContext(ContextMenuCloseContext);
		const shouldShowShortcuts = Accessibility.showContextMenuShortcuts;
		const hasShortcut = Boolean(shortcut);
		const shouldShowShortcut = hasShortcut && shouldShowShortcuts;
		const shortcutKey = typeof shortcut === 'string' && shortcut.length === 1 ? shortcut : undefined;
		const inFlightRef = useRef(false);
		const handlePress = useCallback(
			(event: PressEvent) => {
				if (disabled) return;
				if (inFlightRef.current) return;
				inFlightRef.current = true;
				try {
					onSelect?.(event);
				} finally {
					if (closeOnSelect) {
						closeMenu();
					}
					queueMicrotask(() => {
						inFlightRef.current = false;
					});
				}
			},
			[closeMenu, closeOnSelect, disabled, onSelect],
		);
		const labelContent = children ?? label;
		return (
			<AriaMenuItem
				ref={forwardedRef}
				onPress={handlePress}
				isDisabled={disabled}
				shouldCloseOnSelect={closeOnSelect}
				className={clsx(styles.item, className, {
					[styles.danger]: danger,
					[styles.disabled]: disabled,
					[styles.itemWithShortcut]: shouldShowShortcut,
				})}
				textValue={label || textValueFromNode(children)}
				data-menu-shortcut={shortcutKey}
				data-flx="ui.action-menu.context-menu.menu-item.item.press"
			>
				<div
					className={hint ? styles.itemLabelContainer : styles.itemLabel}
					data-flx="ui.action-menu.context-menu.menu-item.item-label"
				>
					{hint ? (
						<>
							<div className={styles.itemLabelText} data-flx="ui.action-menu.context-menu.menu-item.item-label-text">
								{labelContent}
							</div>
							<div className={styles.itemHint} data-flx="ui.action-menu.context-menu.menu-item.item-hint">
								{hint}
							</div>
						</>
					) : (
						<>
							{labelContent}
							{hasShortcut && (
								<span
									className={shouldShowShortcut ? styles.itemShortcut : styles.itemShortcutHidden}
									aria-hidden={shouldShowShortcut ? undefined : true}
									data-flx="ui.action-menu.context-menu.menu-item.item-shortcut"
								>
									{shortcut}
								</span>
							)}
						</>
					)}
				</div>
				{icon && !shouldShowShortcut && (
					<div className={styles.itemIcon} data-flx="ui.action-menu.context-menu.menu-item.item-icon">
						{icon}
					</div>
				)}
			</AriaMenuItem>
		);
	},
);

MenuItem.displayName = 'MenuItem';

interface SubMenuProps {
	label: string;
	disabled?: boolean;
	hint?: string;
	danger?: boolean;
	children?: React.ReactNode;
	render?: () => React.ReactNode;
	onTriggerSelect?: () => void;
}

export const SubMenu = React.forwardRef<HTMLDivElement, SubMenuProps>(
	({label, disabled, hint, danger = false, children, render, onTriggerSelect}, forwardedRef) => {
		const {i18n} = useLingui();
		const portalHost = usePortalHost();
		const submenuDepth = useContext(SubMenuDepthContext);
		const portalContainer = submenuDepth === 0 ? resolvePortalHost(portalHost) : undefined;
		const handleTriggerPress = useCallback(() => {
			if (disabled) return;
			onTriggerSelect?.();
		}, [disabled, onTriggerSelect]);
		const handleLabelClick = useCallback(
			(event: React.MouseEvent<HTMLDivElement>) => {
				if (disabled || !onTriggerSelect) return;
				const target = event.target as HTMLElement;
				if (target.closest('[data-submenu-caret="true"]')) {
					return;
				}
				event.preventDefault();
				event.stopPropagation();
				onTriggerSelect();
			},
			[disabled, onTriggerSelect],
		);
		const handleLabelKeyDown = useCallback(
			(event: React.KeyboardEvent<HTMLDivElement>) => {
				if (disabled || !onTriggerSelect) return;
				if (isKeyboardActivationKey(event.key)) {
					event.preventDefault();
					onTriggerSelect();
				}
			},
			[disabled, onTriggerSelect],
		);
		const handleMenuAction = useCallback((_key: React.Key) => {
			return false;
		}, []);
		return (
			<AriaSubmenuTrigger data-flx="ui.action-menu.context-menu.sub-menu.aria-submenu-trigger">
				<AriaMenuItem
					ref={forwardedRef}
					isDisabled={disabled}
					className={clsx(styles.item, {
						[styles.disabled]: disabled,
						[styles.danger]: danger,
					})}
					textValue={label}
					onAction={onTriggerSelect ? handleTriggerPress : undefined}
					data-flx="ui.action-menu.context-menu.sub-menu.item"
				>
					<div
						className={clsx(styles.itemLabelContainer)}
						onClick={handleLabelClick}
						onKeyDown={handleLabelKeyDown}
						role="button"
						tabIndex={-1}
						data-flx="ui.action-menu.context-menu.sub-menu.item-label-container.label-click"
					>
						<div className={styles.itemLabelText} data-flx="ui.action-menu.context-menu.sub-menu.item-label-text">
							{label}
						</div>
						{hint && (
							<div className={styles.itemHint} data-flx="ui.action-menu.context-menu.sub-menu.item-hint">
								{hint}
							</div>
						)}
					</div>
					<svg
						className={styles.submenuCaret}
						width="16"
						height="16"
						viewBox="0 0 256 256"
						aria-hidden="true"
						data-submenu-caret="true"
						data-flx="ui.action-menu.context-menu.sub-menu.submenu-caret"
					>
						<path
							fill="currentColor"
							d="M184.49 136.49l-80 80a12 12 0 0 1-17-17L159 128L87.51 56.49a12 12 0 1 1 17-17l80 80a12 12 0 0 1-.02 17"
							data-flx="ui.action-menu.context-menu.sub-menu.path"
						/>
					</svg>
				</AriaMenuItem>
				<AriaPopover
					placement="right top"
					offset={SUBMENU_OFFSET}
					UNSTABLE_portalContainer={portalContainer}
					className={styles.submenuPopover}
					onMouseMoveCapture={stopHoverEventWhenWindowCannotUseHover}
					onMouseOverCapture={stopHoverEventWhenWindowCannotUseHover}
					onPointerMoveCapture={stopHoverEventWhenWindowCannotUseHover}
					onPointerOverCapture={stopHoverEventWhenWindowCannotUseHover}
					data-flx="ui.action-menu.context-menu.sub-menu.submenu-popover"
				>
					{() => (
						<SubMenuDepthContext.Provider value={submenuDepth + 1}>
							<Scroller
								className={styles.submenuScroller}
								contentClassName={styles.menuScrollerContent}
								overflow="auto"
								fade={false}
								key={`context-submenu-scroller-${label}`}
								data-flx="ui.action-menu.context-menu.sub-menu.submenu-scroller"
							>
								<AriaMenu
									className={styles.ariaMenu}
									aria-label={i18n._(SUBMENU_DESCRIPTOR)}
									autoFocus="first"
									selectionMode="none"
									onAction={handleMenuAction}
									data-flx="ui.action-menu.context-menu.sub-menu.aria-menu"
								>
									{render ? render() : children}
								</AriaMenu>
							</Scroller>
						</SubMenuDepthContext.Provider>
					)}
				</AriaPopover>
			</AriaSubmenuTrigger>
		);
	},
);

SubMenu.displayName = 'SubMenu';

export const MenuSeparator: React.FC = observer(() => {
	return <AriaSeparator className={styles.separator} data-flx="ui.action-menu.context-menu.menu-separator.separator" />;
});

interface CheckboxItemProps {
	label?: string;
	checked: boolean;
	onCheckedChange: (checked: boolean) => void;
	disabled?: boolean;
	icon?: React.ReactNode;
	children?: React.ReactNode;
	danger?: boolean;
	closeOnChange?: boolean;
}

export const CheckboxItem = React.forwardRef<HTMLDivElement, CheckboxItemProps>(
	(
		{label, checked, onCheckedChange, disabled, icon: _icon, children, danger = false, closeOnChange = false},
		forwardedRef,
	) => {
		const closeMenu = useContext(ContextMenuCloseContext);
		const handleAction = useCallback(() => {
			if (disabled) return;
			onCheckedChange(!checked);
			if (closeOnChange) {
				closeMenu();
			}
		}, [checked, closeMenu, closeOnChange, disabled, onCheckedChange]);
		return (
			<SelectableMenuItem
				ref={forwardedRef}
				selectionMode="multiple"
				selected={checked}
				onAction={handleAction}
				isDisabled={disabled}
				shouldCloseOnSelect={closeOnChange}
				className={clsx(styles.item, styles.checkboxItem, {
					[styles.disabled]: disabled,
					[styles.danger]: danger,
				})}
				textValue={label || textValueFromNode(children)}
				data-flx="ui.action-menu.context-menu.checkbox-item.item"
			>
				<div className={styles.itemLabel} data-flx="ui.action-menu.context-menu.checkbox-item.item-label">
					{children || label}
				</div>
				<div
					className={styles.checkboxIndicator}
					aria-hidden="true"
					data-flx="ui.action-menu.context-menu.checkbox-item.checkbox-indicator"
				>
					<div
						className={clsx(styles.checkbox, {
							[styles.checkboxChecked]: checked,
						})}
						data-flx="ui.action-menu.context-menu.checkbox-item.checkbox"
					/>
				</div>
			</SelectableMenuItem>
		);
	},
);

CheckboxItem.displayName = 'CheckboxItem';

interface MenuGroupProps {
	label?: string;
	children?: React.ReactNode;
}

export const MenuGroup: React.FC<MenuGroupProps> = observer(({children}) => {
	const validChildren = React.Children.toArray(children).filter((child): child is React.ReactElement => {
		if (!React.isValidElement(child)) return false;
		if (child.type === React.Fragment && !(child.props as {children?: React.ReactNode}).children) return false;
		return true;
	});
	if (validChildren.length === 0) {
		return null;
	}
	return (
		<AriaMenuSection className={styles.group} data-flx="ui.action-menu.context-menu.menu-group.group">
			{validChildren}
		</AriaMenuSection>
	);
});
export const ContextMenu: React.FC = observer(() => {
	const portalHost = usePortalHost();
	if (isMobileExperienceEnabled()) {
		return null;
	}
	const contextMenu = ContextMenuState.contextMenu;
	if (!contextMenu) return null;
	return createPortal(
		<RootContextMenu
			key={contextMenu.id}
			contextMenu={contextMenu}
			data-flx="ui.action-menu.context-menu.root-context-menu"
		/>,
		resolvePortalHost(portalHost),
	);
});
